'use client'

import { Fragment, useState } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { BanknoteArrowUp, Check, FileText, Handshake, Truck } from 'lucide-react'
import { formatDateLong, getVendorPrimaryColor, resolveColorHex } from '@/utils/util'
import { Button } from '@/components/elements/Button'

type TermsType = 'delivery' | 'payment' | 'service'

interface TermsDrawerProps {
  isOpen: boolean
  onClose: () => void
  type: TermsType | null
  terms: {
    delivery?: string
    payment?: string
    service?: string
  }
  vendor?: any
}

const TYPE_META = {
  delivery: { title: 'Хүргэлтийн нөхцөл', eyebrow: 'Хүргэлт', Icon: Truck },
  payment: { title: 'Төлбөрийн нөхцөл', eyebrow: 'Төлбөр', Icon: BanknoteArrowUp },
  service: { title: 'Үйлчилгээний нөхцөл', eyebrow: 'Үйлчилгээ', Icon: Handshake },
} as const

const TABS: { id: TermsType; label: string; Icon: typeof Truck }[] = [
  { id: 'delivery', label: 'Хүргэлт', Icon: Truck },
  { id: 'payment', label: 'Төлбөр', Icon: BanknoteArrowUp },
  { id: 'service', label: 'Үйлчилгээ', Icon: Handshake },
]

export default function TermsDrawer({ isOpen, onClose, type, terms, vendor }: TermsDrawerProps) {
  const [activeType, setActiveType] = useState<TermsType>(type ?? 'delivery')
  const [prevType, setPrevType] = useState<TermsType | null>(type)

  if (type && type !== prevType) {
    setPrevType(type)
    setActiveType(type)
  }

  const meta = TYPE_META[activeType]
  const HeaderIcon = meta.Icon
  const rawContent = terms[activeType]
  const hasContent = !!rawContent && rawContent.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim().length > 0
  const primaryHex = resolveColorHex(getVendorPrimaryColor(vendor))

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 flex justify-end">
          <TransitionChild
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="relative flex w-full max-w-115 flex-col overflow-hidden bg-white shadow-2xl rounded-l-2xl">
              <div className="sticky top-0 z-10 flex h-14 items-center justify-between gap-3 border-b border-border bg-white/95 px-4 backdrop-blur">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-white" style={{ backgroundColor: primaryHex }}>
                    <HeaderIcon className="h-4.5 w-4.5" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <div className="mb-1 text-[10px] font-semibold uppercase leading-none tracking-[0.14em] text-muted-foreground">{meta.eyebrow}</div>
                    <div className="truncate font-gilroy text-[15px] font-semibold leading-none tracking-[-0.005em] text-foreground">{meta.title}</div>
                  </div>
                </div>
                <button type="button" onClick={onClose} aria-label="Close" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              <div className="px-3 pt-3">
                <div className="flex items-center gap-1 rounded-xl bg-muted p-1">
                  {TABS.map(t => {
                    const active = activeType === t.id
                    return (
                      <button key={t.id} type="button" onClick={() => setActiveType(t.id)} className={`inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg text-[13px] font-medium transition-all ${active ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                        <t.Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
                        {t.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="px-6 pt-5 pb-2">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Дэлгэрэнгүй</div>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain px-6 pb-6">
                {hasContent ? (
                  <div className="prose prose-sm max-w-none text-[14px] leading-6 text-foreground" dangerouslySetInnerHTML={{ __html: rawContent! }} />
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <FileText className="h-8 w-8 text-muted-foreground/60 mb-2" />
                    <p className="text-[13px] text-muted-foreground">Мэдээлэл олдсонгүй</p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-border bg-white p-3">
                <span className="text-[12px] text-muted-foreground">{vendor?.createdAt ? `Дэлгүүр нээгдсэн огноо: ${formatDateLong(new Date(vendor.createdAt))}` : ''}</span>
                <Button tag="button" size="sm" color={getVendorPrimaryColor(vendor)} border={vendor?.border} icon={Check} iconPosition="right" onClick={onClose}>Ойлголоо</Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
